import React, { useState, useEffect } from 'react';
import { X, Save, Wrench } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

const DOCUMENT_CATEGORIES = [
  "Policy", "Process / Procedure", "Risk", "Regulatory", "Control",
  "Context", "Interview Notes", "Diagram or Visual", "Flowchart",
  "Organizational Chart", "Process Map", "Screenshot", "Other"
];

interface Document {
  id: number;
  category: string;
  description: string;
  analysis_instructions: string;
  metadata?: Record<string, unknown>;
}

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  document: Document | null;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({ isOpen, onClose, onSuccess, document }) => {
  const [formData, setFormData] = useState({ description: '', category: '', analysis_instructions: '' });
  const [isToolDoc, setIsToolDoc] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document) {
      console.log('📝 Edit Modal - Received document:', document);
      console.log('📝 Analysis Instructions:', document.analysis_instructions);
      const meta = document.metadata || {};
      setFormData({
        description: document.description || '',
        category: document.category || 'Other',
        analysis_instructions: document.analysis_instructions || '',
      });
      setIsToolDoc(meta.is_tool_documentation === true || meta.is_tool_documentation === 'true');
    }
  }, [document]);

  const handleSubmit = async () => {
    if (!document) return;
    setLoading(true);
    try {
      const meta = { ...(document.metadata || {}) };
      if (isToolDoc) {
        meta.is_tool_documentation = true;
        meta.tool_platform = 'Workday';
      } else {
        delete meta.is_tool_documentation;
        delete meta.tool_platform;
      }
      await api.put(`/api/documents/${document.id}`, { ...formData, metadata: meta });
      toast.success('Document updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update document.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Edit Document Metadata</h2>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select value={formData.category} onChange={e => setFormData(f => ({ ...f, category: e.target.value }))} className="w-full p-2 border rounded mt-1">
              {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea placeholder="Document description..." value={formData.description} onChange={e => setFormData(f => ({...f, description: e.target.value}))} className="w-full p-2 border rounded mt-1" rows={3}></textarea>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Analysis Instructions (Optional)</label>
            <textarea placeholder="e.g., Focus only on slides 3-5..." value={formData.analysis_instructions} onChange={e => setFormData(f => ({...f, analysis_instructions: e.target.value}))} className="w-full p-2 border rounded mt-1" rows={3}></textarea>
          </div>

          {/* Workday Documentation Toggle */}
          <div className={`rounded-lg border p-3 ${isToolDoc ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isToolDoc}
                onChange={e => setIsToolDoc(e.target.checked)}
                className="w-4 h-4 accent-blue-600"
              />
              <Wrench className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">This is Workday documentation</span>
            </label>
            {isToolDoc && (
              <p className="text-xs text-blue-600 mt-2 ml-6">
                SOP generation will compare against this documentation and tag any content sourced from it as <strong>[Incorporated from Workday documentation]</strong>.
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border">Cancel</button>
          <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 rounded-lg bg-electric-blue text-white disabled:opacity-50 flex items-center space-x-2">
            <Save className="w-4 h-4" />
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDocumentModal;
